/* Guide terminology for customer-facing content supplied by Shopify.
   Link destinations, product identifiers, data attributes and assets stay intact. */
(() => {
  const term = /(?<![\p{L}\p{N}_])e[-‐‑ ]?books?(?![\p{L}\p{N}_])/giu;
  const candidate = /(?<![\p{L}\p{N}_])e[-‐‑ ]?books?(?![\p{L}\p{N}_])/iu;
  const excluded = 'script,style,noscript,textarea,code,pre,[contenteditable="true"],.diy-trust__review';
  const attrs = ['alt', 'title', 'aria-label', 'placeholder'];
  const label = word => {
    const replacement = /s$/i.test(word) ? 'guides' : 'guide';
    return word === word.toUpperCase() ? replacement.toUpperCase() : /^[A-Z]/.test(word) ? replacement[0].toUpperCase() + replacement.slice(1) : replacement;
  };
  const rewrite = text => {
    if (!candidate.test(text)) return text;
    return text.split(/(https?:\/\/[^\s<>]+)/g).map(part => {
      if (/^https?:\/\//.test(part)) return part;
      return part.replace(/\b([lLdD])['’](e[-‐‑ ]?books?)\b/gi, (_, article, word) => {
        const prefix = article.toLowerCase() === 'l' ? 'le' : 'de';
        return (article === article.toUpperCase() ? prefix[0].toUpperCase() + prefix.slice(1) : prefix) + ' ' + label(word);
      }).replace(/\b(cet|Cet|CET)\s+(?=e[-‐‑ ]?book\b)/gi, word => word === word.toUpperCase() ? 'CE ' : /^[A-Z]/.test(word) ? 'Ce ' : 'ce ')
        .replace(/\b(an|An|AN)\s+(?=e[-‐‑ ]?book\b)/gi, word => word === word.toUpperCase() ? 'A ' : /^[A-Z]/.test(word) ? 'A ' : 'a ')
        .replace(term, label);
    }).join('');
  };
  // Remove decorative CTA arrows and section eyebrows throughout the store,
  // including labels inserted later by product selection or Shopify sections.
  const arrowGlyphs = /[↗↘↙↖←→↑↓⇢➜➔➝⟶⟵➡➞➟➠]/gu;
  const cleanDecorations = root => {
    const element = root.nodeType === Node.ELEMENT_NODE ? root : root.parentElement;
    if (!element || element.closest('script,style,noscript,textarea,code,pre,[contenteditable="true"]')) return;
    if (element.matches('[class*="eyebrow"]')) { element.remove(); return; }
    element.querySelectorAll('[class*="eyebrow"]').forEach(eyebrow => eyebrow.remove());
    const controls = new Set(element.querySelectorAll('a,button,[role="button"]'));
    const parentControl = element.closest('a,button,[role="button"]');
    if (parentControl) controls.add(parentControl);
    for (const control of controls) {
      if (control.matches('[data-trust-prev],[data-trust-next],.slider-button,.swiper-button-prev,.swiper-button-next')) continue;
      const walker = document.createTreeWalker(control, NodeFilter.SHOW_TEXT);
      const texts = [];
      while (walker.nextNode()) texts.push(walker.currentNode);
      const labelled = texts.some(text => !text.parentElement.closest('svg,.visually-hidden,.sr-only') && text.nodeValue.replace(arrowGlyphs, '').trim());
      if (!labelled) continue; // Icon-only navigation controls keep their meaning.
      for (const text of texts) {
        if (text.parentElement.closest('svg,script,style,blockquote,code,pre')) continue;
        const updated = text.nodeValue.replace(/\s*[↗↘↙↖←→↑↓⇢➜➔➝⟶⟵➡➞➟➠]\s*/gu, ' ');
        if (updated === text.nodeValue) continue;
        if (!updated.trim() && text.parentElement.tagName === 'SPAN' && text.parentElement.childNodes.length === 1) text.parentElement.remove();
        else text.nodeValue = updated.trimEnd();
      }
      control.querySelectorAll('svg.icon-arrow,svg.icon-button-arrow').forEach(icon => {
        const wrapper = icon.parentElement;
        icon.remove();
        if (wrapper.tagName === 'SPAN' && !wrapper.textContent.trim() && !wrapper.children.length) wrapper.remove();
      });
    }
  };
  const normalize = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!node.parentElement || node.parentElement.closest(excluded)) return;
      const updated = rewrite(node.nodeValue);
      if (updated !== node.nodeValue) node.nodeValue = updated;
    } else if (node.nodeType === Node.ELEMENT_NODE && !node.closest(excluded)) {
      for (const attr of attrs) {
        if (!node.hasAttribute(attr)) continue;
        const current = node.getAttribute(attr), updated = rewrite(current);
        if (updated !== current) node.setAttribute(attr, updated);
      }
    }
  };
  const scan = root => {
    cleanDecorations(root);
    normalize(root);
    if (root.nodeType !== Node.ELEMENT_NODE || root.closest(excluded)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode: node => node.nodeType === Node.ELEMENT_NODE && node.matches(excluded) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    while (walker.nextNode()) normalize(walker.currentNode);
  };
  const start = () => {
    scan(document.body);
    document.title = rewrite(document.title);
    document.querySelectorAll('meta[name="description"],meta[property="og:title"],meta[property="og:description"],meta[name="twitter:title"],meta[name="twitter:description"]').forEach(meta => {
      meta.content = rewrite(meta.content);
    });
    new MutationObserver(changes => {
      for (const change of changes) {
        if (change.type === 'childList') change.addedNodes.forEach(scan);
        else { cleanDecorations(change.target); normalize(change.target); }
      }
    }).observe(document.body, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:attrs});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
